import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { authApi } from "@/lib/api";
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import AuthCallback from "@/pages/AuthCallback";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import { VerifyEmailPage, ResetPasswordPage } from "@/pages/AuthPages";
import FaqPage from "@/pages/FaqPage";
import ContactPage from "@/pages/ContactPage";
import TrainingCenter from "@/pages/TrainingCenter";
import TermsPage from "@/pages/TermsPage";
import AboutPage from "@/pages/AboutPage";
import PricingPage from "@/pages/PricingPage";
import OwnerDashboard from "@/pages/OwnerDashboard";
import SignupPage from "@/pages/SignupPage";
import ReferralLeaderboardPage from "@/pages/ReferralLeaderboardPage";
import PerformancePage from "@/pages/PerformancePage";

function ProtectedRoute({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState(location.state?.user ? "ok" : "checking");
  const [user, setUser] = useState(location.state?.user || null);

  useEffect(() => {
    if (state === "ok") return;
    let mounted = true;
    (async () => {
      try {
        const u = await authApi.me();
        if (!mounted) return;
        setUser(u);
        setState("ok");
      } catch {
        if (!mounted) return;
        setState("no");
        navigate("/");
      }
    })();
    return () => { mounted = false; };
  }, [state, navigate]);

  if (state === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#090A0F] text-slate-300">
        <div className="font-mono text-xs uppercase tracking-[0.3em]">Authenticating…</div>
      </div>
    );
  }
  if (state !== "ok") return null;
  return React.cloneElement(children, { user });
}

function AppRouter() {
  const location = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/faq" element={<FaqPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/training" element={<TrainingCenter />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/admin" element={<OwnerDashboard />} />
      <Route path="/owner" element={<OwnerDashboard />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/join" element={<SignupPage />} />
      <Route path="/leaderboard" element={<ReferralLeaderboardPage />} />
      <Route path="/referrals" element={<ReferralLeaderboardPage />} />
      <Route path="/performance" element={<PerformancePage />} />
      <Route path="/stats" element={<PerformancePage />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/auth/verify" element={<VerifyEmailPage />} />
      <Route path="/auth/reset" element={<ResetPasswordPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App aq-grain">
      <BrowserRouter>
        <AppRouter />
        <Toaster theme="dark" position="top-right" />
      </BrowserRouter>
    </div>
  );
}
